import { create } from "zustand";
import { api } from "../api/greenclub";

const useAuthStoreImpl = create((set, get) => ({
  user: null,
  loading: true, // Inicia como true para aguardar a verificação inicial

  // Funções de mutação do estado
  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),

  // Verificar autenticação ao carregar a página
  checkAuth: async () => {
    set({ loading: true });
    const token = localStorage.getItem("authToken"); // Corrigido para usar 'authToken'

    if (token) {
      console.log("[AUTH] Token encontrado. Verificando perfil...");
      try {
        // A chamada à API já envia o token no header
        const profileResponse = await api.getProfile();
        if (profileResponse.success && profileResponse.user) {
          console.log(
            "[AUTH] Sessão restaurada com sucesso:",
            profileResponse.user
          );
          set({ user: profileResponse.user, loading: false });
          return;
        }
      } catch (error) {
        console.warn(
          "[AUTH] Falha ao restaurar sessão com token:",
          error.message
        );
        localStorage.removeItem("authToken"); // Limpa token inválido
      }
    }

    console.log("[AUTH] Nenhuma sessão ativa encontrada.");
    set({ user: null, loading: false });
  },

  // Registrar novo usuário no sistema
  registerUser: async (userData) => {
    set({ loading: true });

    try {
      await api.createPublicRegistration(userData);

      // Recarregar dados após registro com pequeno delay para garantir consistência
      await new Promise((resolve) => setTimeout(resolve, 500));
      await get().checkAuth();
    } catch (error) {
      console.error("Erro no registro:", error);

      // Log detailed error information for debugging
      if (error instanceof Error) {
        console.error("Registration error details:", {
          name: error.name,
          message: error.message,
          userData: { ...userData, senha: "[REDACTED]" }, // Don't log passwords
        });
      }

      throw error;
    } finally {
      set({ loading: false });
    }
  },

  // Login com email e senha
  login: async (
    email,
    senha,
    portal = import.meta.env.VITE_APP_PORTAL || "paciente"
  ) => {
    console.log("🔑 Auth Store: Iniciando login...", {
      email,
      senha: senha ? `[${senha.length} chars]` : "[EMPTY]",
      portal,
    });

    set({ loading: true });

    try {
      // Tentar login com email/senha via API
      console.log("📡 Auth Store: Chamando API de login...");
      const loginResponse = await api.login(email, senha);

      console.log("📋 Auth Store: Resposta da API:", {
        success: loginResponse?.success,
        hasUser: !!loginResponse?.user,
        userType: loginResponse?.user?.tipo,
        demoMode: loginResponse?.demo_mode,
      });

      if (loginResponse.success && loginResponse.user) {
        // 1. VERIFICAÇÃO DO PORTAL (ANTES DE TUDO)
        // Se o portal for 'paciente' e o tipo de usuário NÃO for 'paciente', bloqueia.
        if (portal === "paciente" && loginResponse.user.tipo !== "paciente") {
          throw new Error("Use o backoffice para acessar com este perfil.");
        }

        // Se o portal for 'backoffice' e o tipo de usuário for 'paciente', bloqueia.
        if (portal === "backoffice" && loginResponse.user.tipo === "paciente") {
          throw new Error(
            "Portal exclusivo para administradores, representantes e clínicas."
          );
        }

        // 2. CRIAÇÃO DO OBJETO DE USUÁRIO
        const user = {
          id: loginResponse.user.id.toString(),
          email: loginResponse.user.email,
          nome: loginResponse.user.nome,
          tipo: loginResponse.user.tipo,
          ativo: loginResponse.user.ativo !== false, // Default to true
          aprovado: loginResponse.user.aprovado === 1,
          encrypted_yw_id: loginResponse.user.encrypted_yw_id,
          criadoEm: loginResponse.user.criadoEm
            ? new Date(loginResponse.user.criadoEm)
            : new Date(),
          atualizadoEm: loginResponse.user.atualizadoEm
            ? new Date(loginResponse.user.atualizadoEm)
            : new Date(),
        };

        // 3. SALVAR TOKEN E ATUALIZAR ESTADO (APENAS SE A VERIFICAÇÃO PASSAR)
        if (loginResponse.token) {
          localStorage.setItem("authToken", loginResponse.token);
          console.log("✅ Auth Store: Token JWT salvo no localStorage.");
        }

        console.log("✅ Auth Store: Login validado e realizado com sucesso:", {
          userId: user.id,
          email: user.email,
          tipo: user.tipo,
          aprovado: user.aprovado,
          portal,
        });

        set({ user, loading: false });
        return user; // Retorna o usuário em caso de sucesso
      }

      console.error("❌ Auth Store: Resposta da API inválida");
      throw new Error("Resposta inválida do servidor");
    } catch (error) {
      set({ loading: false });

      console.error("🚫 Auth Store: Erro no processo de login:", {
        name: error?.name,
        message: error?.message,
        email: email,
        portal,
        timestamp: new Date().toISOString(),
        stack: error?.stack,
      });

      // Ensure error message is properly formatted
      const errorMessage = error?.message || "Erro desconhecido no login";
      const loginError = new Error(errorMessage);
      loginError.name = "LoginError";

      throw loginError;
    }
  },

  // Função auxiliar para login demo (removida - usar login com email/senha)

  logout: () => {
    set({ user: null });
    localStorage.removeItem("authToken");
    // Em um sistema real, também faria logout do Youware
  },
}));

// Exporta o hook e inicializa a verificação de autenticação
export const useAuthStore = useAuthStoreImpl;

// Chama checkAuth() uma vez quando o módulo é carregado pela primeira vez.
// Isso garante que a sessão seja restaurada ao recarregar a página.
console.log(
  "[AUTH] Inicializando store de autenticação e verificando sessão..."
);
useAuthStore.getState().checkAuth();
